"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { createElement, type ReactNode } from "react";
import { Radio, Menu, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TenantSettingsDto } from "@/types/tenant";
import type { SunnyNavItem } from "@/types/sunny";
import {
  Home,
  Calendar,
  Newspaper,
  Images,
  Store,
  MapPin,
  Map,
  Users,
  User,
  Heart,
  Star,
  Music,
  Mic,
  Phone,
  Mail,
  Globe,
  ExternalLink,
  Search,
  Settings,
  Shield,
  Camera,
  Video,
  Tv,
  Sparkles,
  Gift,
  Ticket,
  Flag,
  Building,
  Building2,
  Landmark,
  Coffee,
  Utensils,
  Car,
  Bus,
  Plane,
  Briefcase,
  BookOpen,
  Book,
  FileText,
  Link as LinkIcon,
  Link2,
  Hash,
  AtSign,
  Bell,
  Megaphone,
  MessageCircle,
  Rss,
  Wifi,
  Code,
  Cloud,
  Sun,
  Moon,
  Umbrella,
  Church,
  Compass,
  Navigation,
  Pin,
  ShoppingBag,
  ShoppingCart,
  Headphones,
  Palette,
  Crown,
  Image as ImageIcon,
} from "lucide-react";

const NAV_ICON_MAP: Record<string, LucideIcon> = {
  home: Home,
  calendar: Calendar,
  newspaper: Newspaper,
  radio: Radio,
  images: Images,
  store: Store,
  "map-pin": MapPin,
  map: Map,
  users: Users,
  user: User,
  heart: Heart,
  star: Star,
  music: Music,
  mic: Mic,
  phone: Phone,
  mail: Mail,
  globe: Globe,
  "external-link": ExternalLink,
  search: Search,
  settings: Settings,
  shield: Shield,
  image: ImageIcon,
  camera: Camera,
  video: Video,
  tv: Tv,
  sparkles: Sparkles,
  gift: Gift,
  ticket: Ticket,
  flag: Flag,
  building: Building,
  "building-2": Building2,
  landmark: Landmark,
  coffee: Coffee,
  utensils: Utensils,
  car: Car,
  bus: Bus,
  plane: Plane,
  briefcase: Briefcase,
  "book-open": BookOpen,
  book: Book,
  "file-text": FileText,
  link: LinkIcon,
  "link-2": Link2,
  hash: Hash,
  "at-sign": AtSign,
  bell: Bell,
  megaphone: Megaphone,
  "message-circle": MessageCircle,
  rss: Rss,
  wifi: Wifi,
  code: Code,
  cloud: Cloud,
  sun: Sun,
  moon: Moon,
  umbrella: Umbrella,
  church: Church,
  compass: Compass,
  navigation: Navigation,
  pin: Pin,
  "shopping-bag": ShoppingBag,
  "shopping-cart": ShoppingCart,
  headphones: Headphones,
  palette: Palette,
  crown: Crown,
};

function renderNavIcon(icon: string | null | undefined, className = "w-4 h-4"): ReactNode {
  const raw = (icon || "").trim();
  if (!raw) return null;
  const Icon = NAV_ICON_MAP[raw.toLowerCase()];
  if (!Icon) return null;
  return createElement(Icon, { className });
}

function isNavPathActive(pathname: string, url: string): boolean {
  const u = (url || "").trim();
  if (!u) return false;
  if (u === "/") return pathname === "/";
  const normalized = u.length > 1 && u.endsWith("/") ? u.slice(0, -1) : u;
  if (pathname === normalized) return true;
  return pathname.startsWith(`${normalized}/`);
}

interface SunnyHeaderProps {
  tenant: TenantSettingsDto;
  navItems: SunnyNavItem[];
}

export function SunnyHeader({ tenant, navItems }: SunnyHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();

  const settings = tenant.settings;
  const siteName = (settings.siteName as string | undefined) || tenant.name || "Sunny 106.5";
  const logoCircleUrl = (settings.logoCircleUrl as string | undefined) || (settings.logoUrl as string | undefined);
  const logoFontFamily = (settings.logoFontFamily as string | undefined) || "Bungee";

  const displayNav = [...navItems]
    .filter((item) => (item.url || "").trim())
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const linkClassName =
    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2";

  return (
    <header className="border-b border-slate-200 bg-white shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-primary">
              {logoCircleUrl ? (
                <Image
                  src={logoCircleUrl}
                  alt={`${siteName} logo`}
                  width={40}
                  height={40}
                  className="h-full w-full object-contain"
                  unoptimized
                />
              ) : (
                <Radio className="h-5 w-5 text-white" />
              )}
            </div>
            <span
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: logoFontFamily || undefined }}
            >
              {siteName}
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {displayNav.map((item) => {
              const active = isNavPathActive(pathname, item.url);
              const external = item.url.startsWith("http");
              const className = cn(
                linkClassName,
                active
                  ? "bg-primary/10 text-primary"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-900",
              );
              if (external) {
                return (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {renderNavIcon(item.icon)}
                    {item.label}
                  </a>
                );
              }
              return (
                <Link key={item.id} href={item.url} className={className}>
                  {renderNavIcon(item.icon)}
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <button
            type="button"
            className="rounded-lg p-2 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-slate-700" />
            ) : (
              <Menu className="h-6 w-6 text-slate-700" />
            )}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="border-t border-slate-200 py-4 md:hidden" aria-label="Mobile">
            {displayNav.map((item) => {
              const active = isNavPathActive(pathname, item.url);
              const external = item.url.startsWith("http");
              const className = cn(
                linkClassName,
                active
                  ? "bg-primary/10 text-primary"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-900",
              );
              if (external) {
                return (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {renderNavIcon(item.icon)}
                    {item.label}
                  </a>
                );
              }
              return (
                <Link
                  key={item.id}
                  href={item.url}
                  className={className}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {renderNavIcon(item.icon)}
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
}
